import { useEffect, useState, useCallback } from "react";
import { api, currency } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Bits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Pencil, Minus, Boxes } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", unit: "adet", stock: 0, min_stock: 0, cost: 0 };

export default function Inventory() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [adjMat, setAdjMat] = useState(null);
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");

  const load = useCallback(() => api.get("/materials").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (m) => { setForm(m); setEditId(m.id); setOpen(true); };
  const submit = async () => {
    if (!form.name) return toast.error("İsim zorunlu");
    const payload = { ...form, stock: Number(form.stock), min_stock: Number(form.min_stock), cost: Number(form.cost) };
    if (editId) await api.put(`/materials/${editId}`, payload); else await api.post("/materials", payload);
    toast.success("Kaydedildi"); setOpen(false); load();
  };
  const del = async (m) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/materials/${m.id}`); load(); };
  const openAdjust = (m) => { setAdjMat(m); setDelta(0); setReason(""); setAdjustOpen(true); };
  const doAdjust = async () => {
    await api.post(`/materials/${adjMat.id}/adjust`, { delta: Number(delta), reason });
    toast.success("Stok güncellendi"); setAdjustOpen(false); load();
  };

  return (
    <div className="p-8">
      <PageHeader eyebrow="Depo" title="Stok ve Hammadde" subtitle="Hammadde stoklarını ve minimum eşikleri yönetin"
        actions={<Button data-testid="material-new-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2" />Yeni Malzeme</Button>} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Malzeme Düzenle" : "Yeni Malzeme"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>İsim</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Birim</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="adet, kg, m, ..." /></div>
              <div><Label>Birim Maliyet ($)</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlangıç Stok</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
              <div><Label>Minimum Stok</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="material-save-btn" onClick={submit} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Stok Hareketi — {adjMat?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">Mevcut stok: <span className="font-bold text-slate-800">{adjMat?.stock} {adjMat?.unit}</span></div>
            <div><Label>Değişim (+ giriş / − çıkış)</Label><Input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} /></div>
            <div><Label>Açıklama</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Satın alma, sarf, düzeltme..." /></div>
          </div>
          <DialogFooter><Button onClick={doAdjust} className="bg-blue-600 hover:bg-blue-700">Uygula</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {list.length === 0 ? (
        <EmptyState icon={Boxes} title="Henüz malzeme yok" description="Hammaddelerinizi ekleyerek başlayın." />
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-widest">Malzeme</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Birim</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Stok</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Min. Stok</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Maliyet</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Durum</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((m) => {
                const low = m.min_stock > 0 && m.stock <= m.min_stock;
                return (
                  <TableRow key={m.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell>{m.unit}</TableCell>
                    <TableCell className="font-semibold">{m.stock}</TableCell>
                    <TableCell className="text-slate-500">{m.min_stock}</TableCell>
                    <TableCell>{currency(m.cost)}</TableCell>
                    <TableCell>
                      {low ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Kritik</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Yeterli</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" title="Stok hareketi" onClick={() => openAdjust(m)}><Minus className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(m)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(m)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
